import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaWeightHanging } from "react-icons/fa";
import { Row, Col, Card, CardTitle, CardBody, CardFooter } from "reactstrap";

import api from "../../../../../services/api";
import { weightFormat } from "../../../../../utils/weightFormat";
import { formatCurrencyInteiro } from "../../../../../utils/formatCurrency";
import { unitFormat } from "../../../../../utils/unitFormat";
import { useCompanies } from "../../../../../hooks/companies"; // Importa o hook useCompanies
import { useDateFilter } from "../../../../../hooks/datefilter";
import moment from "moment";

export default function ChartTotalizadores({
  deferLoad = false,
  onRegisterFetch,
}) {
  const [totalizadores, setTotalizadores] = useState([]);
  const { empresa, transportadora, arrayParams } = useCompanies(); // Obtém as seleções de empresa e transportadora
  const { startDate, endDate } = useDateFilter();
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadTotalizadores = useCallback(async () => {
    try {
      const arrayT = [];

      const params = {};

      if (arrayParams.embarcadores) {
        params.embarcadores = arrayParams.embarcadores;
      }

      if (arrayParams.transportadoras) {
        params.transportadoras = arrayParams.transportadoras;
      }

      // PO1-3215: com o período global definido, as médias usam esse período
      // (sem ele, o backend usa os últimos 11 meses, inclusive no divisor de entregas/dia).
      if (startDate && endDate) {
        params.dataInicial = moment(startDate).format("YYYY-MM-DD 00:00:00");
        params.dataFinal = moment(endDate).format("YYYY-MM-DD 23:59:59");
      }

      // media de peso embarcado por cte
      const responseTotalizadores = await api.get(
        "/dashboard/custo/totalizadores",
        { params }
      );

      if (responseTotalizadores.status === 200) {
        arrayT.push({
          titulo: "Peso",
          subtitulo: "Peso médio por entrega",
          valor: weightFormat(responseTotalizadores.data.pesoMedio),
          icon: (
            <div className="info-icon text-center icon-warning">
              <i className="fas weight-hanging">
                <FaWeightHanging icon="weight-hanging" />
              </i>
            </div>
          ),
        });
        arrayT.push({
          titulo: "Valor de NF",
          subtitulo: "Valor médio por entrega",
          valor: formatCurrencyInteiro(responseTotalizadores.data.valorMedio),
          icon: (
            <div className="info-icon text-center icon-primary">
              <i className="tim-icons icon-money-coins" />
            </div>
          ),
        });
        // PO1-2712 (revisão pós-QA): variação vs período anterior saiu daqui
        // — o card "Frete/Entrega" da linha de indicadores do topo
        // (IndicadoresFrete) já cobre essa comparação.
        arrayT.push({
          titulo: "Frete",
          subtitulo: "Frete médio por entrega",
          valor: formatCurrencyInteiro(responseTotalizadores.data.freteMedio),
          icon: (
            <div className="info-icon text-center icon-success">
              <i className="tim-icons icon-delivery-fast" />
            </div>
          ),
        });
        arrayT.push({
          titulo: "Entregas",
          subtitulo: "Qtd média de entregas/dia",
          valor: unitFormat(responseTotalizadores.data.entregaMedia),
          icon: (
            <div className="info-icon text-center icon-danger">
              <i className="tim-icons icon-paper" />
            </div>
          ),
        });
      }

      if (isMounted.current) {
        setTotalizadores(arrayT);
      }
    } catch (error) {
      if (isMounted.current) {
        console.error("Erro ao carregar totalizadores de custo:", error);
        setTotalizadores([]);
      }
    }
  }, [arrayParams.embarcadores, arrayParams.transportadoras, startDate, endDate]);

  useEffect(() => {
    if (onRegisterFetch) {
      onRegisterFetch(loadTotalizadores);
    }

    if (!deferLoad) {
      loadTotalizadores();
    }
  }, [deferLoad, empresa, loadTotalizadores, onRegisterFetch, transportadora]);

  return (
    <Row>
      {totalizadores.map((totalizador, index) => (
        <Col lg="3" md="6" key={index.toString()}>
          <Card
            style={{ boxShadow: "0px 1px 20px 0px rgb(0, 0, 0)" }}
            className="card-stats"
          >
            <CardBody>
              <Row>
                <Col xs="4">
                  <div className="info-icon text-center icon-warning">
                    {totalizador.icon}
                  </div>
                </Col>
                <Col xs="8">
                  <div className="numbers">
                    <CardTitle tag="h4">{totalizador.titulo}</CardTitle>
                    <CardTitle tag="h3">{totalizador.valor}</CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <hr />
              <div className="stats">
                <i className="tim-icons icon-sound-wave" /> {totalizador.subtitulo}
              </div>
            </CardFooter>
          </Card>
        </Col>
      ))}
    </Row>
  );
}
